import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Camera, Paperclip, Trash, FileArrowDown, ImageSquare, FilePdf,
} from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Foto- och filbilagor för en ÄTA.
 * - Öppnar mobilkamera direkt via `capture="environment"` på iOS/Android.
 * - Visar tumnaglar i grid, PDF som lista.
 * - Skapar signed blob-URL:er för visning (använder sessionscookie via api).
 */
export default function AtaAttachments({ ataId, readOnly = false, canEdit = true, onCountChange }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState({}); // file_id -> object URL
  const previewsRef = useRef(previews);
  useEffect(() => { previewsRef.current = previews; }, [previews]);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!ataId) return;
    setLoading(true);
    try {
      const r = await api.get(`/ata/${ataId}/files`);
      setFiles(r.data || []);
      onCountChange?.(r.data?.length || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte hämta bilagor");
    } finally {
      setLoading(false);
    }
  }, [ataId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  // Ladda ner bilder som blob-URL:er för visning i <img> (löser iOS ITP-cookie-problem)
  useEffect(() => {
    const controllers = [];
    files.forEach((f) => {
      if (!f.is_image || previewsRef.current[f.file_id]) return;
      const ctrl = new AbortController();
      controllers.push(ctrl);
      api.get(`/ata/${ataId}/files/${f.file_id}/download`, {
        responseType: "blob",
        signal: ctrl.signal,
      }).then((r) => {
        const url = URL.createObjectURL(r.data);
        setPreviews((p) => ({ ...p, [f.file_id]: url }));
      }).catch(() => {});
    });
    return () => controllers.forEach((c) => c.abort());
  }, [files, ataId]);

  // Städa blob-URLs vid unmount
  useEffect(() => {
    return () => {
      Object.values(previewsRef.current).forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* noop */ }
      });
    };
  }, []);

  const uploadFiles = async (fileList) => {
    if (!ataId || !fileList?.length) return;
    setUploading(true);
    try {
      for (const file of fileList) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await api.post(`/ata/${ataId}/files`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          toast.error(`${file.name}: ${err.response?.data?.detail || "uppladdning misslyckades"}`);
        }
      }
      toast.success(`${fileList.length} fil(er) uppladdade`);
      await load();
    } finally {
      setUploading(false);
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Radera ${f.original_name}?`)) return;
    try {
      await api.delete(`/ata/${ataId}/files/${f.file_id}`);
      toast.success("Bilaga raderad");
      // Släpp blob-URL
      const url = previews[f.file_id];
      if (url) { try { URL.revokeObjectURL(url); } catch { /* noop */ } }
      setPreviews((p) => {
        const next = { ...p };
        delete next[f.file_id];
        return next;
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte radera");
    }
  };

  const openFile = async (f) => {
    try {
      const r = await api.get(`/ata/${ataId}/files/${f.file_id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      // Släpp URL efter 30s
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* noop */ } }, 30000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte öppna filen");
    }
  };

  const images = files.filter((f) => f.is_image);
  const others = files.filter((f) => !f.is_image);

  return (
    <div data-testid="ata-attachments" className="space-y-3">
      {!readOnly && canEdit && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="ata-take-photo-button"
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            className="tk-btn-primary !py-2 !px-3 text-sm inline-flex items-center gap-2"
          >
            <Camera size={16} weight="fill" /> Ta foto
          </button>
          <button
            type="button"
            data-testid="ata-attach-file-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="tk-btn-outline !py-2 !px-3 text-sm inline-flex items-center gap-2"
          >
            <Paperclip size={16} /> Bifoga fil
          </button>
          {uploading && <span className="text-xs text-text-muted self-center">Laddar upp…</span>}

          <input
            ref={cameraInputRef}
            data-testid="ata-camera-input"
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
          />
          <input
            ref={fileInputRef}
            data-testid="ata-file-input"
            type="file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
          />
        </div>
      )}

      {loading ? (
        <p className="text-xs text-text-muted">Laddar bilagor…</p>
      ) : files.length === 0 ? (
        <p className="text-xs text-text-muted">
          Inga bilagor{readOnly ? "." : " ännu. Ta ett foto eller bifoga en fil ovan."}
        </p>
      ) : (
        <>
          {images.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2" data-testid="ata-image-grid">
              {images.map((f) => (
                <div
                  key={f.file_id}
                  data-testid={`ata-image-${f.file_id}`}
                  className="relative group aspect-square rounded-md overflow-hidden bg-black/5 border border-app"
                >
                  {previews[f.file_id] ? (
                    <img
                      src={previews[f.file_id]}
                      alt={f.original_name}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => openFile(f)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-text-muted">
                      <ImageSquare size={28} />
                    </div>
                  )}
                  {!readOnly && canEdit && (
                    <button
                      type="button"
                      data-testid={`ata-delete-file-${f.file_id}`}
                      onClick={() => deleteFile(f)}
                      className="absolute top-1 right-1 p-1.5 bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition"
                      aria-label="Radera foto"
                    >
                      <Trash size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {others.length > 0 && (
            <ul className="space-y-1" data-testid="ata-file-list">
              {others.map((f) => (
                <li
                  key={f.file_id}
                  data-testid={`ata-file-${f.file_id}`}
                  className="flex items-center gap-2 text-sm border border-app rounded-md px-3 py-2"
                >
                  <FilePdf size={18} className="text-rust shrink-0" />
                  <button
                    type="button"
                    onClick={() => openFile(f)}
                    className="flex-1 text-left truncate hover:underline"
                  >
                    {f.original_name}
                  </button>
                  <span className="text-xs text-text-muted whitespace-nowrap">
                    {Math.round((f.size || 0) / 1024)} kB
                  </span>
                  <button
                    type="button"
                    onClick={() => openFile(f)}
                    className="text-text-muted hover:text-text-primary"
                    aria-label="Ladda ner"
                  >
                    <FileArrowDown size={16} />
                  </button>
                  {!readOnly && canEdit && (
                    <button
                      type="button"
                      data-testid={`ata-delete-file-${f.file_id}`}
                      onClick={() => deleteFile(f)}
                      className="text-text-muted hover:text-rust"
                      aria-label="Radera"
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
